/* eslint-disable react-hooks/exhaustive-deps */
import { Links, Meta, Outlet, Scripts, ScrollRestoration, useLoaderData } from '@remix-run/react';
import { data, LinksFunction } from '@remix-run/node';
import { json, type LoaderArgs } from '@remix-run/server-runtime';
import { useFavicon, useTitle } from 'ahooks';
import BigCalendarCss from 'react-big-calendar/lib/css/react-big-calendar.css?url';
import { Toaster } from 'react-hot-toast';
import InputOTP from '@UI/components/InputOTP/input-otp.css?url';
import rcSelect from '@UI/components/Select/styles/rc-select.css?url';
import rcPagination from '@UI/styles/rc-pagination.css?url';
import rcpicker from '@UI/styles/rc-picker.css?url';
import rcTable from '@UI/styles/rc-table.css?url';
import stylesheet from '@UI/styles/tailwind.css?url';
import customStyles from '@UI/styles/custom.css?url';
import { getPublicKeys } from '@controls/utils/env';
import { PublicEnv } from '@UI/components/PublicEnv/public-env';
import { tolgee } from '@shared/@services/translation/tool';
import BaseLayout from '@UI/layouts/base';
import { TolgeeNextProvider } from '@shared/@services/translation/tool/provider';
import { getTokenAtServer } from '@shared/@controls/utils/session.server';

import '@UI/styles/custom.css?url';
import { defaultLanguage } from '@shared/@services/translation/tool/constants/default';
import { i18nCookie } from '@shared/@services/translation/helpers';
// Code protection disabled - allowing source code inspection
// import { useCodeProtection } from '@shared/@services/packages/trustedo-remixjs-security/src/react';

export const links: LinksFunction = () => [
  { rel: 'stylesheet', href: InputOTP },
  { rel: 'stylesheet', href: stylesheet },
  { rel: 'stylesheet', href: BigCalendarCss },
  { rel: 'stylesheet', href: rcpicker },
  { rel: 'stylesheet', href: rcPagination },
  { rel: 'stylesheet', href: rcTable },
  { rel: 'stylesheet', href: rcSelect },
  { rel: 'stylesheet', href: rcSelect },
  { rel: 'stylesheet', href: customStyles },
];

export const meta = () => {
  return [
    {
      charset: 'utf-8',
      viewport: 'width=device-width,initial-scale=1',
    },
  ];
};

export async function loader({ request }: LoaderArgs) {
  const url = new URL(request.url);
  const language = url.searchParams.get('lng');
  const locale = language || tolgee.getPendingLanguage() || defaultLanguage;
  const staticData = await tolgee.loadRequired();
  tolgee.changeLanguage(locale);

  const token = await getTokenAtServer(request);

  const systemTitle = staticData.find((item) => item.namespace === 'common')?.data?.[
    'system_title'
  ];
  if (token === '/') {
    return json({
      systemTitle,
      tolgeeStaticData: staticData,
      currentUser: null,
      getPublicKey: getPublicKeys(),
      locale,
    });
  }

  return data(
    {
      systemTitle,
      getPublicKey: getPublicKeys(),
      locale,
      tolgeeStaticData: staticData,
    },
    {
      headers: { 'Set-Cookie': await i18nCookie.serialize(locale) },
    },
  );
}

export const handle = {
  i18n: ['common', 'error-message'],
};

export default function App() {
  const { getPublicKey, tolgeeStaticData, locale, systemTitle } = useLoaderData<typeof loader>();
  const { publicKeys } = getPublicKey;
  // Code protection disabled - allowing source code inspection and debugging
  // useCodeProtection({
  //   disableConsole: true,
  //   disableInspect: true,
  //   detectDevTools: true,
  //   antiTamper: true,
  // });

  // This hook will change the i18n instance language to the current locale
  // detected by the loader, this way, when we do something to change the
  // language, this locale will change and i18next will load the correct
  // translation files
  useTitle(systemTitle || 'Contributions Citoyennes');

  useFavicon('/favicon.ico');

  return (
    <html lang={locale}>
      <head>
        <meta charSet='utf-8' />
        <meta name='viewport' content='width=device-width,initial-scale=1' />
        <Meta />
        <Links />
        <title>Contributions Citoyennes</title>
        {/* SystemPay compatibility workaround - must load before any other scripts */}
        <script
          dangerouslySetInnerHTML={{
            __html: `
              (function() {
                if (typeof window !== 'undefined' && !window.__SYSTEMPAY_OBJECT_PROTECTION__) {
                  window.__SYSTEMPAY_OBJECT_PROTECTION__ = {
                    freeze: Object.freeze,
                    seal: Object.seal,
                    preventExtensions: Object.preventExtensions
                  };
                }
              })();
            `,
          }}
        />
      </head>
      <body>
        <TolgeeNextProvider
          staticData={tolgeeStaticData}
          language={tolgee.getLanguage() || defaultLanguage}
        >
          <PublicEnv {...publicKeys} />
          <BaseLayout>
            <Outlet />
          </BaseLayout>
          <ScrollRestoration />
          <Scripts />
          <Toaster position='top-right' reverseOrder={false} />
        </TolgeeNextProvider>
      </body>
    </html>
  );
}
